import { animate, prefersReducedMotion } from 'motion';

const FADE_DURATION = 1;
const FADE_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const init = (root: HTMLElement) => {
  const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-tab-button]'));
  const panes = Array.from(root.querySelectorAll<HTMLElement>('[data-tab-pane]'));
  const defaultTab = root.dataset.defaultTab ?? '';

  if (buttons.length === 0 || panes.length === 0) return;

  const fadeIn = (pane: HTMLElement) => {
    if (prefersReducedMotion.current) return;
    animate(
      pane,
      { opacity: [0, 1], transform: ['translateY(4px)', 'translateY(0)'] },
      { duration: FADE_DURATION, ease: FADE_EASE },
    );
  };

  const setActive = (tabId: string, animateIn: boolean): boolean => {
    let matched = false;
    for (const btn of buttons) {
      const isActive = btn.dataset.tabButton === tabId;
      if (isActive) matched = true;
      btn.toggleAttribute('data-active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    }
    for (const pane of panes) {
      const wasActive = pane.hasAttribute('data-active');
      const isActive = pane.dataset.tabPane === tabId;
      pane.toggleAttribute('data-active', isActive);
      if (isActive) {
        pane.removeAttribute('hidden');
        if (animateIn && !wasActive) fadeIn(pane);
      } else {
        pane.setAttribute('hidden', '');
      }
    }
    return matched;
  };

  const readUrlTab = (): string => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') ?? '';
  };

  const initialTab = readUrlTab();
  if (initialTab && initialTab !== defaultTab && !setActive(initialTab, false)) {
    setActive(defaultTab, false);
  }

  const activate = (btn: HTMLButtonElement, focus: boolean) => {
    const tabId = btn.dataset.tabButton;
    if (!tabId) return;
    setActive(tabId, true);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    window.history.pushState({ tab: tabId }, '', url.toString());
    if (focus) btn.focus();
    btn.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  };

  for (const btn of buttons) {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      activate(btn, false);
    });

    btn.addEventListener('keydown', (event) => {
      const currentIndex = buttons.indexOf(btn);
      let nextIndex = -1;
      switch (event.key) {
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
          break;
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % buttons.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = buttons.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      activate(buttons[nextIndex], true);
    });
  }

  window.addEventListener('popstate', () => {
    const tabId = readUrlTab() || defaultTab;
    setActive(tabId, true);
  });
};

const root = document.querySelector<HTMLElement>('[data-main-learn]');
if (root) init(root);
